export interface ApiErrorPayload {
  code: string;
  message: string;
  requestId: string;
  retryable?: boolean;
}

export class ClaimApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ClaimApiError";
    this.code = payload.code;
    this.requestId = payload.requestId;
    this.status = status;
  }
}

export interface ClaimSession {
  id: string;
  knowledgeBase: {
    id: string;
    name: string;
    summary: string;
    updateFrequency: string;
  };
  expiresAt: string;
  phonePolicy: "must_match_order" | "first_verified_phone";
  maskedOrderPhone: string | null;
  status: "awaiting_phone" | "awaiting_sms" | "redeemed" | "expired" | "locked" | "failed";
}

export interface SmsChallenge {
  smsChallengeId: string;
  expiresIn: number;
  retryAfter: number;
  debugCode?: string;
}

export interface RedeemResult {
  knowledgeBase: { id: string; name: string; summary: string };
  entitlement: { id: string; startsAt: string; endsAt: string; status: string };
  maskedPhone: string;
  appLink: string;
}

const configuredBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
const resolvedBase = configuredBase
  ?? (import.meta.env.DEV ? "http://127.0.0.1:8791/v1" : undefined);
if (!resolvedBase) {
  throw new Error(
    "缺少 VITE_API_BASE_URL：非开发环境构建必须显式配置 API 地址，禁止静默回退 staging。",
  );
}
export const apiBaseUrl = resolvedBase.replace(/\/$/u, "");

async function request<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": crypto.randomUUID(),
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json()) as T | ApiErrorPayload;
  if (!response.ok) throw new ClaimApiError(response.status, payload as ApiErrorPayload);
  return payload as T;
}

export function lookupHumanCode(humanCode: string): Promise<ClaimSession> {
  return request("/claim/lookup", { humanCode });
}

export function lookupQrToken(qrToken: string): Promise<ClaimSession> {
  return request("/claim/lookup", { qrToken });
}

export function requestClaimSms(claimSessionId: string, phone: string): Promise<SmsChallenge> {
  return request("/claim/sms/request", { claimSessionId, phone });
}

export function redeemClaim(
  claimSessionId: string,
  smsChallengeId: string,
  code: string,
): Promise<RedeemResult> {
  return request("/claim/redeem", { claimSessionId, smsChallengeId, code });
}
